import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import {
  CustomInput,
  CustomSelect,
  CustomTextarea,
  CustomToggle,
} from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { AgentFrameApiProvider, useAgentFrameApi } from '../Services/AgentFrameApiProvider';

import { ThematicApiProvider, useThematicApi } from '../Services/ThematicApiProvider';
import { isEmpty } from '../utils/validationUtil';

const FRAME_TYPES_OPTIONS = [
  { value: 'CRITERIA', label: 'Criteria Based' },
  { value: 'TOPIC', label: 'Topic Based' },
  { value: 'THEMATIC', label: 'Thematics Based' },
];

function AgentFrameContent({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const { organizationId, agentFrameId } = useParams();
  const { showAlert } = useAlert();

  const { getAgentFrameById, createAgentFrame, updateAgentFrame } = useAgentFrameApi();
  const { getThematic } = useThematicApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isActive, setIsActive] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [frameType, setFrameType] = useState<string>('CRITERIA');
  const [topic, setTopic] = useState<string>('');
  const [criteria, setCriteria] = useState<string>('');
  const [thematics, setThematics] = useState<string[]>([]);
  const [thematicsOptions, setThematicOptions] = useState<{ value: string; label: string }[]>([]);
  const [instruction, setInstruction] = useState<string>('');
  const [example, setExample] = useState<string>('');

  useEffect(() => {
    const isNewFrame = agentFrameId === 'new-agent-frame';
    setIsNew(isNewFrame);
    setIsDisabled(!canEdit);

    async function fetchData() {
      if (!isNewFrame) {
        setLoading(true);
        try {
          const response = await getAgentFrameById(agentFrameId);
          setName(response.name);
          setFrameType(response.frame_type);
          setTopic(response.topic);
          setCriteria(response.criteria);
          setThematics(response.thematics);
          setInstruction(response.instruction);
          setExample(response.example);
          setIsActive(response.is_active === true);
        } catch (error) {
          showAlert('fail', 'Failed to fetch agent frame');
        } finally {
          setLoading(false);
        }
      }
    }

    fetchData();
  }, [agentFrameId]);

  useEffect(() => {
    async function fetchThematics() {
      try {
        const data = await getThematic(0, 100);
        const options = data.items.map((item) => ({
          value: item.key,
          label: item.name,
        }));

        setThematicOptions(options);
        console.log('thematicsOptions', thematicsOptions);
      } catch (error) {
        console.error('Failed to load thematics:', error);
      }
    }
    fetchThematics();
  }, []);

  const onSave = async (): Promise<void> => {
    if (isEmpty(name)) {
      showAlert('fail', 'Please enter agent frame name');
      return;
    }

    if (isEmpty(frameType)) {
      showAlert('fail', 'Please select agent frame type');
      return;
    }

    if (isEmpty(instruction)) {
      showAlert('fail', 'Please enter instruction');
      return;
    }

    if (isEmpty(criteria) && frameType === 'CRITERIA') {
      showAlert('fail', 'Please enter criteria');
      return;
    }

    if (isEmpty(topic) && frameType === 'TOPIC') {
      showAlert('fail', 'Please enter topic');
      return;
    }

    if ((!thematics || thematics.length === 0) && frameType === 'THEMATIC') {
      showAlert('fail', 'Please select thematic');
      return;
    }

    const data = {
      name: name,
      frame_type: frameType,
      topic: topic,
      criteria: criteria,
      thematics: thematics,
      instruction: instruction,
      example: example,
      is_active: isActive,
    };

    setLoading(true);

    try {
      if (isNew) {
        await createAgentFrame(data);
        showAlert('success', 'Agent frame was created successfully');
      } else {
        await updateAgentFrame(agentFrameId, data);
        showAlert('success', 'Agent frame was updated successfully');
      }
      navigate(`/organizations/${organizationId}/agent-frames`, { state: { organizationId } });
    } catch (error) {
      showAlert('fail', 'Failed to save agent frame');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = (): void => {
    navigate(`/organizations/${organizationId}/agent-frames`, { state: { organizationId } });
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{name}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Agent Frame Name</div>
                  <div className="small-font">Enter agent frame name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="chat-name"
                    label="Agent Frame Name"
                    placeholder="Enter agent frame name"
                    type="text"
                    icon={<i className="fa-solid fa-comments"></i>}
                    value={name}
                    onChange={(id, value) => setName(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Agent Frame Type</div>
                  <div className="small-font">Select agent frame type</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomSelect
                    id="frame-type"
                    label="Frame Type"
                    options={FRAME_TYPES_OPTIONS}
                    value={frameType}
                    onChange={(value) => {
                      setFrameType(value as string);
                    }}
                    multiSelect={false}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              {frameType === 'CRITERIA' && (
                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Criteria</div>
                    <div className="small-font">Add agent frame criteria</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomTextarea
                      id="criteria"
                      label="Criteria"
                      name="criteria"
                      addedText={criteria}
                      onChange={(value) => setCriteria(value)}
                      resizable={false}
                      disabled={isDisabled}
                      height="200px"
                    />
                  </div>
                </div>
              )}

              {frameType === 'TOPIC' && (
                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Topic</div>
                    <div className="small-font">Add agent frame topic</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomTextarea
                      id="topic"
                      label="Topic"
                      name="topic"
                      addedText={topic}
                      onChange={(value) => setTopic(value)}
                      resizable={false}
                      disabled={isDisabled}
                      height="200px"
                    />
                  </div>
                </div>
              )}

              {frameType === 'THEMATIC' && (
                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Thematics</div>
                    <div className="small-font">Select thematics</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="thematics-select"
                      label="Thematics"
                      options={thematicsOptions}
                      onChange={(items) => setThematics(items as string[])}
                      value={thematics}
                      multiSelect={true}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              )}

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Instruction</div>
                  <div className="small-font">Add agent frame instruction</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomTextarea
                    id="instruction"
                    label="Instruction"
                    name="instruction"
                    addedText={instruction}
                    onChange={(value) => setInstruction(value)}
                    resizable={false}
                    disabled={isDisabled}
                    height="200px"
                  />
                </div>
              </div>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Example</div>
                  <div className="small-font">Add convesation example</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomTextarea
                    id="example"
                    label="Converation Example"
                    name="Example"
                    addedText={example}
                    onChange={(value) => setExample(value)}
                    resizable={false}
                    disabled={isDisabled}
                    height="200px"
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate Agent Frame</div>
                  <div className="small-font">
                    Set agent frame to be active or inactive to enable or disable it
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={false}
                  />
                </div>
              </div>
            </div>

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AgentFrame({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <AgentFrameApiProvider>
        <ThematicApiProvider>
          <AgentFrameContent canEdit={canEdit} />
        </ThematicApiProvider>
      </AgentFrameApiProvider>
    </ConfirmationProvider>
  );
}

export default AgentFrame;
